// IndexedDB persistence for the local-folder auto-sync feature.
//
// Stores one record per (event, session): the picked FileSystemDirectoryHandle
// (structured-cloneable, so it survives a page reload), the dedupe manifest,
// the pending upload queue and user settings. This is what lets auto-sync (and
// the plain manual "choose local folder" flow) resume after a refresh without
// forcing the user back through the OS folder picker.
//
// Keyed by `storageKey`, which defaults to the plain eventId (the organiser's
// own EventPage session) but can be overridden - e.g. by the upload-link page,
// which passes `${eventId}:${token}` so a link visitor's auto-sync state never
// collides with the organiser's own EventPage state (or another link's state)
// in the same browser.
const DB_NAME = 'cm-local-folder-sync';
const DB_VERSION = 2;
const STORE_NAME = 'folderSyncState';

let dbPromise = null;

function openDB() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (event) => {
      const db = req.result;
      // v1 stores keyed the record by eventId alone. Every existing v1 record
      // is necessarily an organiser's own EventPage session (upload links
      // didn't exist yet), whose storageKey is just String(eventId) - the
      // same value the record was already keyed by - so the old rows are
      // carried forward under the new keyPath instead of being dropped,
      // preserving already-armed auto-sync folders across this upgrade
      // rather than silently un-arming every event on the next load.
      if (event.oldVersion < 2 && db.objectStoreNames.contains(STORE_NAME)) {
        const oldStore = req.transaction.objectStore(STORE_NAME);
        const getAllReq = oldStore.getAll();
        getAllReq.onsuccess = () => {
          const oldRecords = getAllReq.result || [];
          db.deleteObjectStore(STORE_NAME);
          const newStore = db.createObjectStore(STORE_NAME, { keyPath: 'storageKey' });
          for (const rec of oldRecords) {
            newStore.put({ ...rec, storageKey: String(rec.eventId) });
          }
        };
        return;
      }
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'storageKey' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

// Runs `requestFactory(store)` inside a transaction of the given mode and
// resolves with that IDBRequest's `.result` once the transaction commits.
async function runRequest(mode, requestFactory) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, mode);
    const store = tx.objectStore(STORE_NAME);
    const req = requestFactory(store);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
    tx.oncomplete = () => resolve(req.result);
  });
}

export async function getFolderSyncState(eventId, storageKey = String(eventId)) {
  return runRequest('readonly', (store) => store.get(String(storageKey)));
}

// Every record, regardless of storageKey - used by autoSyncRegistry.js to
// enforce "only one auto-sync at a time device-wide" and to drive the
// floating auto-sync indicator, both of which need to look across every
// event/link's state, not just one storageKey at a time.
export async function getAllFolderSyncStates() {
  return runRequest('readonly', (store) => store.getAll());
}

export async function putFolderSyncState(record, storageKey = String(record.eventId)) {
  return runRequest('readwrite', (store) => store.put({ ...record, storageKey: String(storageKey), updatedAt: Date.now() }));
}

export async function deleteFolderSyncState(eventId, storageKey = String(eventId)) {
  return runRequest('readwrite', (store) => store.delete(String(storageKey)));
}

// Hard reset: deletes the entire local-folder-sync database, for when
// persisted state is suspected corrupted (e.g. a stored dirHandle that throws
// on every use, or a stuck/inconsistent record deleteFolderSyncState alone
// doesn't clear up). Unlike deleteFolderSyncState (which only clears one
// event/link's record), this wipes *every* event's auto-sync state on this
// device/browser - every armed folder anywhere in the app will need to be
// re-selected afterwards. Closes the cached connection first, since
// indexedDB.deleteDatabase() blocks (fires onblocked, not onerror) while any
// connection is still open.
export async function resetFolderSyncDb() {
  if (dbPromise) {
    try {
      const db = await dbPromise;
      db.close();
    } catch { /* connection never opened successfully - nothing to close */ }
    dbPromise = null;
  }
  return new Promise((resolve, reject) => {
    const req = indexedDB.deleteDatabase(DB_NAME);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
    // Another tab still has the DB open - can't force it closed from here,
    // but don't hang the caller waiting for that tab to release it.
    req.onblocked = () => resolve();
  });
}
